import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Coffee, Loader2, Clock, Hash, Percent, ChevronRight } from 'lucide-react';
import { useProfile } from '@core/contexts/ProfileContext';
import { useIdentity } from '@core/contexts/IdentityContext';
import { Header } from '@core/components/Header';
import { Card } from '@core/components/Card';
import { Button } from '@core/components/Button';
import toast from 'react-hot-toast';
import {
  getBundlePlans,
  getMyBundles,
  requestBundle,
  type BundlePlan,
  type BundlePass,
} from '@core/api/bundles';

// ============================================================================
// Plan Card — available plans for purchase
// ============================================================================

function PlanCard({ plan, onBuy, buying }: {
  plan: BundlePlan;
  onBuy: (planId: string) => void;
  buying: boolean;
}) {
  const discount = plan.regularPrice > 0
    ? Math.round((1 - plan.price / plan.regularPrice) * 100)
    : 0;

  return (
    <Card className="space-y-3">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Coffee className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-text-main">{plan.name}</h3>
          {plan.description && (
            <p className="text-sm text-text-muted mt-0.5">{plan.description}</p>
          )}
        </div>
      </div>

      {/* Details */}
      <div className="flex flex-wrap gap-2 text-xs text-text-muted">
        <span className="flex items-center gap-1 bg-secondary/10 rounded-full px-2 py-1">
          <Hash size={12} /> {plan.totalCount} шт.
        </span>
        <span className="flex items-center gap-1 bg-secondary/10 rounded-full px-2 py-1">
          <Clock size={12} /> {plan.validDays} дней
        </span>
        {plan.earnPoints && (
          <span className="flex items-center gap-1 bg-secondary/10 rounded-full px-2 py-1">
            <Percent size={12} /> +баллы
          </span>
        )}
      </div>

      {/* Price + CTA */}
      <div className="flex items-center justify-between pt-2 border-t border-border">
        <div className="flex items-baseline gap-2">
          <span className="text-xl font-extrabold text-text-main">
            {plan.price.toLocaleString('ru-RU')}₽
          </span>
          {discount > 0 && (
            <>
              <span className="text-sm text-text-muted line-through">
                {plan.regularPrice.toLocaleString('ru-RU')}₽
              </span>
              <span className="text-xs font-bold text-green-600 bg-green-100 px-1.5 py-0.5 rounded">
                -{discount}%
              </span>
            </>
          )}
        </div>
        <Button
          size="sm"
          onClick={() => onBuy(plan.id)}
          disabled={buying}
        >
          {buying ? 'Подождите...' : 'Хочу'}
        </Button>
      </div>
    </Card>
  );
}

// ============================================================================
// My Pass Card — active/pending passes
// ============================================================================

function PassCard({ pass, onClick }: {
  pass: BundlePass;
  onClick: () => void;
}) {
  const used = pass.totalCount - pass.remainingCount;
  const progress = pass.totalCount > 0 ? (used / pass.totalCount) * 100 : 0;

  const statusLabel: Record<string, string> = {
    PENDING: 'Ожидает оплаты',
    ACTIVE: 'Активен',
    COMPLETED: 'Завершён',
    EXPIRED: 'Истёк',
    CANCELLED: 'Отменён',
  };

  const statusColor: Record<string, string> = {
    PENDING: 'text-yellow-600 bg-yellow-50',
    ACTIVE: 'text-green-600 bg-green-50',
    COMPLETED: 'text-blue-600 bg-blue-50',
    EXPIRED: 'text-slate-500 bg-slate-50',
    CANCELLED: 'text-red-500 bg-red-50',
  };

  const daysLeft = pass.expiresAt
    ? Math.max(0, Math.ceil((new Date(pass.expiresAt).getTime() - Date.now()) / (24 * 60 * 60 * 1000)))
    : null;

  return (
    <Card className="space-y-3" onClick={onClick}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Coffee className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="font-bold text-text-main">{pass.plan.name}</h3>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusColor[pass.status] ?? 'text-slate-500 bg-slate-50'}`}>
              {statusLabel[pass.status] ?? pass.status}
            </span>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-text-muted mt-1" />
      </div>

      {/* Progress */}
      {pass.status === 'ACTIVE' && (
        <>
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-text-muted">
              <span>Использовано: {used} из {pass.totalCount}</span>
              <span>Осталось: {pass.remainingCount}</span>
            </div>
            <div className="h-2 bg-secondary/20 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-500 rounded-full"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
          {daysLeft !== null && (
            <p className="text-xs text-text-muted">
              {daysLeft > 0 ? `Осталось ${daysLeft} дней` : 'Истекает сегодня'}
            </p>
          )}
        </>
      )}
    </Card>
  );
}

// ============================================================================
// Main Page
// ============================================================================

export function BundlesPage() {
  // Клиент — из сессии входа (K-002): абонементы работают в обоих входах. Telegram ID — необязательная сверка
  // (есть только у входа Telegram; в PWA — null и в запрос не уходит).
  const { user: profileUser, loading: profileLoading } = useProfile();
  const { telegramId } = useIdentity();
  const userId = profileUser?.id ?? null;
  const navigate = useNavigate();

  const [plans, setPlans] = useState<BundlePlan[]>([]);
  const [passes, setPasses] = useState<BundlePass[]>([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    if (!userId) {
      if (!profileLoading) setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        const [plansData, passesData] = await Promise.all([
          getBundlePlans(telegramId),
          getMyBundles(telegramId),
        ]);
        setPlans(plansData);
        setPasses(passesData);
      } catch (err) {
        console.error('[BundlesPage] Failed to load data:', err);
      } finally {
        setLoading(false);
      }
    };

    void loadData();
  }, [userId, telegramId, profileLoading]);

  const handleBuy = async (planId: string) => {
    if (!userId) return;

    try {
      setBuying(true);
      const result = await requestBundle(planId, telegramId);
      toast.success(`Абонемент «${result.planName}» создан! Покажите QR на кассе.`);
      navigate(`/bundles/${result.passId}/qr`);
    } catch (err) {
      // Toast already shown by api client
      console.error('[BundlesPage] Failed to request bundle:', err);
    } finally {
      setBuying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-page text-text-main">
        <Header title="Абонементы" />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-primary" size={32} />
        </div>
      </div>
    );
  }

  // Active/pending passes first
  const activePasses = passes.filter((p) => p.status === 'ACTIVE' || p.status === 'PENDING');
  const pastPasses = passes.filter((p) => p.status !== 'ACTIVE' && p.status !== 'PENDING');

  return (
    <div className="min-h-screen bg-page text-text-main pb-20">
      <Header title="Абонементы" />

      <div className="p-4 space-y-6">
        {/* My active passes */}
        {activePasses.length > 0 && (
          <section className="space-y-3">
            <h2 className="text-lg font-bold text-text-main">Мои абонементы</h2>
            {activePasses.map((pass) => (
              <PassCard
                key={pass.id}
                pass={pass}
                onClick={() => navigate(`/bundles/${pass.id}/qr`)}
              />
            ))}
          </section>
        )}

        {/* Available plans */}
        {plans.length > 0 && (
          <section className="space-y-3">
            <h2 className="text-lg font-bold text-text-main">
              {activePasses.length > 0 ? 'Купить ещё' : 'Доступные абонементы'}
            </h2>
            {plans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                onBuy={handleBuy}
                buying={buying}
              />
            ))}
          </section>
        )}

        {/* Past passes */}
        {pastPasses.length > 0 && (
          <section className="space-y-3">
            <h2 className="text-lg font-bold text-text-main">История</h2>
            {pastPasses.map((pass) => (
              <PassCard
                key={pass.id}
                pass={pass}
                onClick={() => navigate(`/bundles/${pass.id}/qr`)}
              />
            ))}
          </section>
        )}

        {/* Empty state */}
        {plans.length === 0 && passes.length === 0 && (
          <div className="text-center py-12">
            <Coffee className="w-12 h-12 mx-auto mb-4 text-text-muted opacity-50" />
            <h3 className="text-lg font-bold text-text-main">Нет абонементов</h3>
            <p className="text-sm text-text-muted mt-1">
              Абонементы пока не доступны
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
